import { useEffect } from 'react';

import { inputActions } from 'app/modules/wallforming/components/input-binding/registry/inputActionsRegistry';
import { InputDevice } from 'app/modules/wallforming/components/input-binding/types/inputDevice';
import { InputTrigger } from 'app/modules/wallforming/components/input-binding/types/inputTrigger';
import {
    DOUBLE_CLICK_MS,
    chordFromKeyboardEvent,
    chordFromMouseEvent
} from 'app/modules/wallforming/components/input-binding/utils/inputChordUtils';
import { isTextInputElement } from 'app/modules/wallforming/components/scenes/objects/utils/sceneUtils';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';

/** Element that records mouse buttons while capturing; clicks elsewhere cancel the capture. */
export const CAPTURE_ZONE_ATTRIBUTE = 'data-input-capture-zone';

/** Element that toggles capture itself and must neither be recorded nor cancel the capture. */
export const CAPTURE_TOGGLE_ATTRIBUTE = 'data-input-capture-toggle';

const isWithin = (event: Event, attribute: string) =>
    event.target instanceof Element && event.target.closest(`[${attribute}]`) !== null;

const suppressEvent = (event: Event) => {
    event.preventDefault();
    event.stopImmediatePropagation();
};

/** Records the next key/mouse chord for the active capture target and suppresses all other handling meanwhile. */
export const useInputCaptureHandler = () => {
    const target = usePlacementStore((state) => state.inputCaptureTarget);

    useEffect(() => {
        if (!target) {
            return;
        }

        const { assignInputBinding, cancelInputCapture } = usePlacementStore.getState();
        const { devices, triggers } = inputActions[target.actionId];
        const allowsClick = triggers.includes(InputTrigger.Click);
        const allowsDoubleClick = triggers.includes(InputTrigger.DoubleClick);
        let pendingPress: { button: number; timeStamp: number; timer?: ReturnType<typeof setTimeout> } | undefined;

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.code === 'Escape') {
                suppressEvent(event);
                cancelInputCapture();
                return;
            }
            if (isTextInputElement(document.activeElement) || isTextInputElement(event.target)) {
                return;
            }
            suppressEvent(event);
            const chord = chordFromKeyboardEvent(event);
            if (chord && devices.includes(InputDevice.Keyboard)) {
                assignInputBinding(target.actionId, target.slot, chord);
            }
        };

        const handleMouseDown = (event: MouseEvent) => {
            if (isWithin(event, CAPTURE_TOGGLE_ATTRIBUTE)) {
                return;
            }
            if (!isWithin(event, CAPTURE_ZONE_ATTRIBUTE)) {
                cancelInputCapture();
                return;
            }
            suppressEvent(event);
            if (!devices.includes(InputDevice.Mouse)) {
                return;
            }

            const assign = (trigger: InputTrigger) =>
                assignInputBinding(target.actionId, target.slot, chordFromMouseEvent(event, trigger));
            const previousPress = pendingPress;
            const isSecondPress =
                previousPress?.button === event.button && event.timeStamp - previousPress.timeStamp <= DOUBLE_CLICK_MS;
            clearTimeout(previousPress?.timer);
            pendingPress = undefined;

            if (isSecondPress && allowsDoubleClick) {
                assign(InputTrigger.DoubleClick);
            } else {
                if (previousPress && allowsClick) {
                    assign(InputTrigger.Click);
                }
                if (!allowsDoubleClick) {
                    assign(InputTrigger.Click);
                    return;
                }

                const press: { button: number; timeStamp: number; timer?: ReturnType<typeof setTimeout> } = {
                    button: event.button,
                    timeStamp: event.timeStamp
                };
                pendingPress = press;
                press.timer = setTimeout(() => {
                    if (pendingPress !== press) {
                        return;
                    }
                    pendingPress = undefined;
                    if (allowsClick) {
                        assign(InputTrigger.Click);
                    }
                }, DOUBLE_CLICK_MS);
            }
        };

        const suppressInZone = (event: MouseEvent) => {
            if (isWithin(event, CAPTURE_ZONE_ATTRIBUTE)) {
                suppressEvent(event);
            }
        };

        window.addEventListener('keydown', handleKeyDown, true);
        window.addEventListener('mousedown', handleMouseDown, true);
        window.addEventListener('click', suppressInZone, true);
        window.addEventListener('contextmenu', suppressInZone, true);

        return () => {
            clearTimeout(pendingPress?.timer);
            window.removeEventListener('keydown', handleKeyDown, true);
            window.removeEventListener('mousedown', handleMouseDown, true);
            window.removeEventListener('click', suppressInZone, true);
            window.removeEventListener('contextmenu', suppressInZone, true);
        };
    }, [target]);
};
